import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatChipSelectionChange } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, fromEvent } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { OutgoingPayment, Permission } from 'src/app/api/models';
import { OutgoingPaymentsService } from 'src/app/api/services';
import { formatStatus } from 'src/app/components/outgoing-payment-status-label/outgoing-payment-status-label.component';
import { PaginationDataSource } from 'src/app/model/pagination-data-source';
import { UserService } from 'src/app/service/user.service';

class OutgoingPaymentQuery {
  status: ('PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED')[];
}

type OutgoingPaymentStatus = OutgoingPaymentQuery['status'][number];

@Component({
  selector: 'app-outgoing-payments-list-page',
  templateUrl: './outgoing-payments-list-page.component.html',
  styleUrls: ['./outgoing-payments-list-page.component.scss']
})
export class OutgoingPaymentsListPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  @ViewChild('table', { read: ElementRef }) public matTableRef: ElementRef;

  q: OutgoingPaymentQuery = new OutgoingPaymentQuery();
  title = 'Outgoing Payments';

  // Whether the current user may trigger a manual bank-response poll. Gated
  // on the exposed permission, not a role string (design D5), so the check
  // survives both the Payer and legacy payments.create role values.
  canPoll$: Observable<boolean>;
  // Disables the button while a trigger request is in flight.
  polling = false;

  // The four filterable statuses. Labels come from the shared `formatStatus`
  // vocabulary so the filter speaks the same language as the status treatment
  // used elsewhere and can't drift on casing (design D4). An empty selection
  // means "all statuses" — the default view is defined by the nav link, not
  // here (see design D3). Note: this page's status column still renders the raw
  // enum; routing it through app-outgoing-payment-status-label is a follow-up.
  readonly statuses: ReadonlyArray<{ value: OutgoingPaymentStatus; label: string }> =
    (['PENDING', 'IN_PROGRESS', 'SUCCESS', 'FAILED'] as const).map(value => ({
      value,
      label: formatStatus(value)
    }));

  columns = [
    'created_at',
    'invoice_id',
    'amount.amount',
    'to_name',
    'channel',
    'status'
  ];

  dataSource = new PaginationDataSource<OutgoingPayment, OutgoingPaymentQuery>(
    (request, query) => this.outgoingPaymentsService.listAllOutgoingPayments({
      status: query.status,
      after: request.after,
      limit: request.limit
    }),
    { status: [] },
    50,
  );

  constructor(
    private outgoingPaymentsService: OutgoingPaymentsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private snackBar: MatSnackBar
  ) {
    this.canPoll$ = this.userService.hasPermission(Permission.PaymentsPollResponses);
  }

  ngOnInit() {
    this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngDestroy$)).subscribe(params => {
      this.queryBy({
        status: params.getAll('status') as OutgoingPaymentStatus[]
      });
    });
  }

  ngAfterViewInit(): void {
    fromEvent<Event>(this.matTableRef.nativeElement, 'scroll')
      .pipe(debounceTime(700))
      .subscribe((e) => this.onTableScroll(e));
  }

  // Computes the next status selection from the current URL-derived `q.status`
  // and pushes it through `updateQuery` — the URL stays the single source of
  // truth (design D2/D6). Legacy Material chips do not toggle their own
  // selection, so both the mouse `(click)` path and the keyboard SPACE path
  // (via `onChipSelectionChange`) route through here.
  toggleStatus(value: OutgoingPaymentStatus) {
    const next = this.q.status.includes(value)
      ? this.q.status.filter(s => s !== value)
      : [...this.q.status, value];
    this.updateQuery({ status: next });
  }

  // Keyboard SPACE on a selectable chip emits `selectionChange` with
  // `isUserInput: true`. Only those flip the filter; the `isUserInput: false`
  // events that the `[selected]` input emits whenever the URL-derived state
  // re-renders are ignored, otherwise the derived binding would feed back into
  // itself and loop.
  onChipSelectionChange(value: OutgoingPaymentStatus, event: MatChipSelectionChange) {
    if (event.isUserInput) {
      this.toggleStatus(value);
    }
  }

  updateQuery(params: Pick<OutgoingPaymentQuery, 'status'>) {
    // An empty status selection means "all". Pass `null` so the router drops
    // the `status` keys from the URL under `merge` instead of leaving stale
    // values behind (see design D2/task 1.3).
    const queryParams = {
      status: params.status.length ? params.status : null
    };
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  queryBy(params: OutgoingPaymentQuery) {
    this.q = params;
    this.dataSource.queryBy(this.q);
  }

  reset() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {}
    });
  }

  // Fire-and-forget trigger for an immediate bank-response poll (design D6).
  // The backend signal has no completion callback, so we confirm the trigger
  // and never pretend to know when results land; the list is checked back
  // manually (the status filter makes that cheap).
  pollResponses() {
    this.polling = true;
    this.outgoingPaymentsService.pollOutgoingPaymentResponses()
      // takeUntil BEFORE finalize: if the user navigates away before the
      // request resolves, teardown still fires finalize (clearing `polling`)
      // but drops the next/error callbacks, so the root-provided snackbar
      // can't pop on the unrelated destination page (issue #92 finding 3).
      .pipe(takeUntil(this.ngDestroy$), finalize(() => (this.polling = false)))
      .subscribe({
        next: () =>
          this.snackBar.open(
            'Bank response polling triggered — results appear in a few minutes.',
            'OK',
            { duration: 5000 }
          ),
        error: (err) => {
          console.error('failed to trigger bank response polling', err);
          this.snackBar.open('Failed to trigger bank response polling.', 'OK', { duration: 5000 });
        }
      });
  }

  private onTableScroll(e: Event): void {
    // The table is the scroll's target: a scroll event does not bubble.
    const table = e.target;
    if (!(table instanceof HTMLElement)) {
      return;
    }
    const tableViewHeight = table.offsetHeight;
    const tableScrollHeight = table.scrollHeight;
    const scrollLocation = table.scrollTop;
    const scrollThreshold = 500;

    const scrollDownLimit = tableScrollHeight - tableViewHeight - scrollThreshold;
    if (scrollLocation > scrollDownLimit) {
      this.dataSource.fetchNextPage();
    }
  }

  ngOnDestroy() {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
